import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2, Plus, X, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { AppShell } from '../components/Layout';
import { linkedge } from '../lib/api';
import { getErrorMessage } from '../lib/errors';
import {
  billingApi,
  persistPendingAudit,
  readPendingAudit,
  clearPendingAudit,
} from '../lib/billing';
import { toast } from 'sonner';
import { WizardPdfImport } from '../components/wizard/WizardPdfImport';

const STEPS = [
  { key: 'basics', title: 'The Basics', desc: 'Name, role, industry.' },
  { key: 'headline', title: 'Headline', desc: 'Your current 220-char hook.' },
  { key: 'about', title: 'About', desc: 'Your current summary.' },
  { key: 'experience', title: 'Experience', desc: 'Add your roles.' },
  { key: 'skills', title: 'Skills', desc: 'List your current skills.' },
  { key: 'targeting', title: 'Targeting', desc: 'Optional — sharpen the audit.' },
];

function mapImportedProfile(importedProfile, initialUrl) {
  return {
    name: importedProfile.name || '',
    current_title: importedProfile.current_title || '',
    headline: importedProfile.headline || '',
    about: importedProfile.about || '',
    experiences:
      (importedProfile.experiences && importedProfile.experiences.length > 0)
        ? importedProfile.experiences
        : [{ company: '', title: '', dates: '', description: '' }],
    skills: importedProfile.skills || [],
    industry: importedProfile.industry || '',
    education: importedProfile.education || '',
    posts: importedProfile.posts || [],
    photo_url: importedProfile.avatar_url || importedProfile.photo_url || '',
    profile_url: importedProfile.profile_url || initialUrl || null,
    avatar_url: importedProfile.avatar_url || importedProfile.photo_url || null,
    location: importedProfile.location || null,
    connections:
      typeof importedProfile.connections === 'number' ? importedProfile.connections : null,
    followers:
      typeof importedProfile.followers === 'number' ? importedProfile.followers : null,
  };
}

export default function ManualWizardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // ---- Resume from a pending audit (set when user hit a 402 before payment) ----
  // We try to read it once, synchronously, so initial state can be hydrated.
  const resumeRequested = new URLSearchParams(location.search).get('resume') === '1';
  const resumed = resumeRequested ? readPendingAudit() : null;

  const initialUrl = (resumed?.profile_url) || location.state?.url || '';
  const scrape = location.state?.scrape || null;
  const importedProfile = (resumed?.profile) || location.state?.profile || null;
  const fromPdf = !!(resumed || location.state?.fromPdf);
  const fromLinkedin = !!location.state?.fromLinkedin;
  const sourceUrl = location.state?.sourceUrl || '';
  const filename = (resumed?.filename) || location.state?.filename || '';
  const auditContext = (resumed?.auditContext) || location.state?.auditContext || null;
  // Treat LinkedIn import like PDF: jump to review step, show import badge,
  // celebrate the auto-fill instead of warning about sign-in.
  const importedViaState = fromPdf || fromLinkedin;
  // In-wizard PDF import (WizardPdfImport card) — hydrates the wizard in
  // place instead of a Dashboard round-trip.
  const [localImport, setLocalImport] = useState(null);
  const isImported = importedViaState || !!localImport;

  const [step, setStep] = useState(importedViaState ? 4 : 0);

  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(() => {
    if (importedProfile) {
      return mapImportedProfile(importedProfile, initialUrl);
    }
    return {
      name: scrape?.name_guess || '',
      current_title: '',
      headline: '',
      about: '',
      experiences: [{ company: '', title: '', dates: '', description: '' }],
      skills: [],
      industry: '',
      education: '',
      posts: [],
      photo_url: '',
    };
  });
  const [skillInput, setSkillInput] = useState('');
  // Optional Full-Audit-v2 targeting inputs. Missing values are inferred
  // cautiously by the audit engine and labelled as assumptions.
  const [targeting, setTargeting] = useState({
    target_role: '', target_audience: '', target_industry: '',
    target_geography: '', tone: '', discreet_job_search: false,
  });
  const updateTargeting = (patch) => setTargeting((t) => ({ ...t, ...patch }));
  const [me, setMe] = useState(null);

  // Fetch the user's plan once - currently informational only; reserved for
  // future plan-aware copy on the wizard.
  useEffect(() => {
    let active = true;
    billingApi.me().then((d) => { if (active) setMe(d); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const _me = me; // retained for future plan-aware UI; intentionally unused now
  void _me;

  const update = (patch) => setProfile((p) => ({ ...p, ...patch }));
  const updateExp = (idx, patch) =>
    setProfile((p) => ({
      ...p,
      experiences: p.experiences.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
    }));
  const addExp = () => update({ experiences: [...profile.experiences, { company: '', title: '', dates: '', description: '' }] });
  const removeExp = (idx) => update({ experiences: profile.experiences.filter((_, i) => i !== idx) });
  const addSkill = () => {
    const v = skillInput.trim();
    if (!v) return;
    if (profile.skills.includes(v)) return;
    update({ skills: [...profile.skills, v] });
    setSkillInput('');
  };
  const removeSkill = (s) => update({ skills: profile.skills.filter((x) => x !== s) });

  const canNext = () => {
    // For imported profiles (LinkedIn / PDF), only require the core
    // identity (name + current title + industry) on step 0. Optional fields
    // (Headline / About / Skills / Experience) are best-effort review
    // surfaces — the audit pipeline auto-drafts a Suggested About when
    // missing (see /app/backend/about_suggester.py) and infers domain from
    // experience, so we must NOT trap the user behind these gates.
    if (isImported) {
      if (step === 0) return profile.name && profile.current_title && profile.industry;
      return true;
    }
    // Manual entry — keep stricter validation since the user is filling
    // everything from scratch and quality matters for the audit signal.
    if (step === 0) return profile.name && profile.current_title && profile.industry;
    if (step === 1) return profile.headline.length > 0;
    if (step === 2) return profile.about.length > 0;
    if (step === 3) return profile.experiences.length > 0 && profile.experiences[0].company;
    if (step === 4) return profile.skills.length >= 3;
    if (step === 5) return true; // targeting is optional
    return false;
  };

  // Human-readable explanation of what's missing on the current step. Used to
  // surface a hint right next to the (disabled) Next button so the user is
  // never stuck wondering why they can't proceed.
  const missingForStep = () => {
    if (step === 0) {
      const missing = [];
      if (!profile.name) missing.push('Name');
      if (!profile.current_title) missing.push('Current job title');
      if (!profile.industry) missing.push('Industry');
      return missing.length ? `Add ${missing.join(', ')} to continue` : null;
    }
    // Imported flow: no per-step gating beyond step 0 — the AI fills in
    // gaps during the audit. Surface a gentle hint instead of a blocker.
    if (isImported) {
      if (step === 2 && !profile.about) return 'Skip if you don\u2019t have one \u2014 we\u2019ll draft a suggested About in the audit.';
      if (step === 4 && profile.skills.length < 3) return 'Optional \u2014 the audit infers skills from your experience.';
      return null;
    }
    if (step === 1 && !profile.headline) return 'Add your headline to continue';
    if (step === 2 && !profile.about) return 'Add your About / summary to continue';
    if (step === 3 && (!profile.experiences.length || !profile.experiences[0].company))
      return 'Add at least one experience (with company) to continue';
    if (step === 4 && profile.skills.length < 3)
      return `Add ${3 - profile.skills.length} more skill${3 - profile.skills.length === 1 ? '' : 's'} to continue (need 3+)`;
    return null;
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const validateAll = () => {
    if (!profile.name || !profile.current_title || !profile.industry) {
      return { ok: false, step: 0, msg: 'Add your name, title, and industry' };
    }
    // Imported flow only validates core identity. Empty About/Skills are
    // expected on partial LinkedIn scrapes and handled gracefully by the
    // audit pipeline (Suggested About generator + experience-driven skill
    // inference).
    if (isImported) {
      return { ok: true };
    }
    if (!profile.headline) return { ok: false, step: 1, msg: 'Add your headline' };
    if (!profile.about) return { ok: false, step: 2, msg: 'Add your About / Summary' };
    if (!profile.experiences.length || !profile.experiences[0].company)
      return { ok: false, step: 3, msg: 'Add at least one experience' };
    if (profile.skills.length < 3) return { ok: false, step: 4, msg: 'Add at least 3 skills' };
    // Posts are no longer collected at audit-intake. Users can generate
    // researched/prompt posts after the audit from inside the analysis page.
    return { ok: true };
  };

  const onSubmit = async () => {
    const v = validateAll();
    if (!v.ok) {
      toast.error(v.msg);
      setStep(v.step);
      return;
    }
    setSubmitting(true);
    try {
      const isStageV2 = ['student', 'fresher', 'corporate'].includes(auditContext?.status);
      const { id } = await linkedge.analyze({
        profile_url: initialUrl || null,
        profile,
        // Stage + objective set on the Dashboard picker. New (v2) stages
        // go out as career_stage; legacy saved contexts keep old keys.
        ...(auditContext?.status && auditContext?.objective
          ? (isStageV2
            ? {
                career_stage: auditContext.status,
                objective: auditContext.objective,
                target_role: targeting.target_role || null,
                target_audience: targeting.target_audience || null,
                target_industry: targeting.target_industry || null,
                target_geography: targeting.target_geography || null,
                tone: targeting.tone || null,
                discreet_job_search: targeting.discreet_job_search || null,
                // 'auto' → let backend auto-pick; otherwise send explicit slider
                language_slider: (auditContext.language_slider && auditContext.language_slider !== 'auto')
                  ? auditContext.language_slider
                  : null,
              }
            : { status: auditContext.status, objective: auditContext.objective })
          : {}),
      });
      // Audit accepted — clear any pending state so a future success page doesn't
      // try to re-resume an already-consumed audit.
      clearPendingAudit();
      toast.success('Analysis started');
      navigate(`/analysis/${id}`);
    } catch (err) {
      // Quota / plan gate: backend returns 402 with a structured payload.
      // Stash the user's filled state and route them to /pricing. After payment,
      // BillingSuccessPage will offer "Resume your audit" which round-trips here
      // with ?resume=1.
      if (err?.response?.status === 402) {
        persistPendingAudit({
          profile,
          profile_url: initialUrl || null,
          filename,
          fromPdf: true, // ensures we re-enter the review step on resume
          auditContext,
        });
        toast.message('Pick a plan to run your audit. We saved your details.');
        navigate('/pricing?reason=audit-quota');
        return; // keep submitting=true to avoid a flash before navigation
      }
      toast.error(getErrorMessage(err, 'Failed to start analysis'));
      setSubmitting(false);
    }
  };

  // ---- Auto-resume after payment ----
  // When the user comes back from /billing/success?next=audit, the page mounts
  // with ?resume=1 + a hydrated profile. We auto-submit on a tick to spare them
  // the extra click. Validation still runs; if anything went stale, we just
  // surface the wizard normally instead of submitting.
  useEffect(() => {
    if (!resumed) return;
    const t = setTimeout(() => {
      const v = validateAll();
      if (v.ok) {
        toast.success('Resuming your audit\u2026');
        onSubmit();
      } else {
        toast.message('Welcome back \u2014 finish the highlighted step to continue.');
        setStep(v.step);
      }
    }, 250);
    return () => clearTimeout(t);
    // We intentionally run this only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">{isImported ? 'Review your imported profile' : 'Paste your profile'}</h1>
          <p className="text-muted-foreground mt-1">
            {fromLinkedin
              ? 'We auto-imported your profile and last 10 posts from LinkedIn. Review each step, fix anything, then click Audit my profile.'
              : (fromPdf || localImport)
              ? 'We auto-extracted your profile from the PDF. Review each step, fix anything, then click Audit my profile.'
              : 'Fill in the basics manually. We\u2019ll guide you through 5 quick steps.'}
          </p>
          {(fromPdf || localImport) && (
            <Badge variant="secondary" className="mt-3 bg-[hsl(var(--mint))]/40 border border-border" data-testid="wizard-pdf-imported-notice">
              Imported from {localImport?.filename || filename || 'PDF'}
            </Badge>
          )}
          {fromLinkedin && (
            <Badge variant="secondary" className="mt-3 bg-[hsl(var(--mint))]/40 border border-border" data-testid="wizard-linkedin-imported-notice">
              Imported from LinkedIn{sourceUrl ? ` — ${sourceUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}` : ''}
            </Badge>
          )}
          {scrape && !scrape.success && !fromPdf && (
            <Badge variant="secondary" className="mt-3" data-testid="scrape-blocked-notice">
              We couldn&rsquo;t read the input ({scrape.reason}). Please paste your details below.
            </Badge>
          )}
        </div>

        {/* In-wizard PDF import — same consent gate as the Dashboard.
            Hidden once a profile has been imported (either via the
            Dashboard round-trip or right here). */}
        {!isImported && (
          <WizardPdfImport
            initialConsent={location.state?.consent}
            onImported={(p, fname) => {
              setProfile(mapImportedProfile(p, initialUrl));
              setLocalImport({ filename: fname || '' });
              setStep(4);
            }}
          />
        )}

        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          {/* Stepper */}
          <aside className="lg:sticky lg:top-24 self-start">
            <Progress value={progress} className="mb-4 h-1.5" data-testid="wizard-progress" />
            <ol className="space-y-1" data-testid="wizard-stepper">
              {STEPS.map((s, i) => {
                const state = i < step ? 'done' : i === step ? 'current' : 'pending';
                return (
                  <li
                    key={s.key}
                    className={`flex items-start gap-3 p-2 rounded-lg ${state === 'current' ? 'bg-secondary' : ''}`}
                  >
                    <span
                      className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                        state === 'done'
                          ? 'bg-primary text-primary-foreground'
                          : state === 'current'
                          ? 'bg-primary/15 text-primary border border-primary/30'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="text-sm">
                      <p className="font-medium">{s.title}</p>
                      <p className="text-xs text-muted-foreground">{s.desc}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* Form */}
          <Card className="border-border">
            <CardContent className="p-6">
              <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}>
                {step === 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">The basics</h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Name</Label>
                        <Input value={profile.name} onChange={(e) => update({ name: e.target.value })} placeholder="Avery Chen" data-testid="wizard-name-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Current job title</Label>
                        <Input value={profile.current_title} onChange={(e) => update({ current_title: e.target.value })} placeholder="Senior Product Manager" data-testid="wizard-title-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Industry</Label>
                        <Input value={profile.industry} onChange={(e) => update({ industry: e.target.value })} placeholder="B2B SaaS" data-testid="wizard-industry-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Education (optional)</Label>
                        <Input value={profile.education} onChange={(e) => update({ education: e.target.value })} placeholder="MBA, State University" data-testid="wizard-education-input" />
                      </div>
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Headline</h2>
                    <Textarea
                      value={profile.headline}
                      onChange={(e) => update({ headline: e.target.value })}
                      placeholder="Senior Product Manager at Acme | MBA"
                      rows={3}
                      maxLength={300}
                      data-testid="wizard-headline-input"
                    />
                    <p className="text-xs text-muted-foreground font-mono-edge">{profile.headline.length}/220 chars</p>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">About / Summary</h2>
                    <Textarea
                      value={profile.about}
                      onChange={(e) => update({ about: e.target.value })}
                      placeholder="Paste your current About section here. Don't worry about polishing it — we will."
                      rows={10}
                      data-testid="wizard-about-input"
                    />
                    <p className="text-xs text-muted-foreground font-mono-edge">{profile.about.length} chars</p>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-semibold">Experience</h2>
                      <Button variant="secondary" size="sm" onClick={addExp} data-testid="wizard-add-experience">
                        <Plus size={14} className="mr-1" /> Add role
                      </Button>
                    </div>
                    <div className="space-y-4">
                      {profile.experiences.map((exp, idx) => (
                        <Card key={idx} className="border-border">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-mono-edge text-muted-foreground">ROLE {idx + 1}</p>
                              {profile.experiences.length > 1 && (
                                <Button variant="ghost" size="icon" onClick={() => removeExp(idx)} data-testid={`wizard-remove-experience-${idx}`}>
                                  <X size={14} />
                                </Button>
                              )}
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                              <Input placeholder="Company" value={exp.company} onChange={(e) => updateExp(idx, { company: e.target.value })} data-testid={`wizard-exp-company-${idx}`} />
                              <Input placeholder="Title" value={exp.title} onChange={(e) => updateExp(idx, { title: e.target.value })} data-testid={`wizard-exp-title-${idx}`} />
                              <Input placeholder="Dates (e.g., 2022 - Present)" value={exp.dates} onChange={(e) => updateExp(idx, { dates: e.target.value })} data-testid={`wizard-exp-dates-${idx}`} />
                            </div>
                            <Textarea
                              placeholder="Current description (what you do/did)"
                              value={exp.description}
                              onChange={(e) => updateExp(idx, { description: e.target.value })}
                              rows={3}
                              data-testid={`wizard-exp-desc-${idx}`}
                            />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Skills</h2>
                    <p className="text-sm text-muted-foreground">Add the skills currently on your profile. Minimum 3.</p>
                    <div className="flex gap-2">
                      <Input
                        value={skillInput}
                        onChange={(e) => setSkillInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                        placeholder="e.g., Product Strategy"
                        data-testid="wizard-skill-input"
                      />
                      <Button variant="secondary" onClick={addSkill} data-testid="wizard-add-skill">Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="wizard-skills-list">
                      {profile.skills.map((s) => (
                        <Badge key={s} variant="secondary" className="pl-2.5 pr-1.5 py-1 text-sm">
                          {s}
                          <button onClick={() => removeSkill(s)} className="ml-1 hover:text-destructive" aria-label={`Remove ${s}`}>
                            <X size={12} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Targeting <span className="text-sm font-normal text-muted-foreground">(optional, recommended)</span></h2>
                    <p className="text-sm text-muted-foreground">
                      These sharpen your audit. Anything you leave blank is inferred cautiously from your PDF and labelled as an assumption.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Desired role or opportunity</Label>
                        <Input value={targeting.target_role} onChange={(e) => updateTargeting({ target_role: e.target.value })} placeholder="e.g., VP Marketing, SDE intern" data-testid="wizard-target-role-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Target audience</Label>
                        <Input value={targeting.target_audience} onChange={(e) => updateTargeting({ target_audience: e.target.value })} placeholder="e.g., B2B SaaS CMOs, tech recruiters" data-testid="wizard-target-audience-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Preferred industry</Label>
                        <Input value={targeting.target_industry} onChange={(e) => updateTargeting({ target_industry: e.target.value })} placeholder="e.g., Fintech" data-testid="wizard-target-industry-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Target geography</Label>
                        <Input value={targeting.target_geography} onChange={(e) => updateTargeting({ target_geography: e.target.value })} placeholder="e.g., India, US remote" data-testid="wizard-target-geography-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Writing tone</Label>
                        <Input value={targeting.tone} onChange={(e) => updateTargeting({ tone: e.target.value })} placeholder="e.g., confident, warm, direct" data-testid="wizard-tone-input" />
                      </div>
                    </div>
                    <label className="flex items-start gap-2.5 text-sm cursor-pointer" data-testid="wizard-discreet-row">
                      <input
                        type="checkbox"
                        checked={targeting.discreet_job_search}
                        onChange={() => updateTargeting({ discreet_job_search: !targeting.discreet_job_search })}
                        className="mt-0.5 h-4 w-4 accent-primary"
                        data-testid="wizard-discreet-checkbox"
                      />
                      <span>I want to remain discreet about job searching (recommendations will avoid overt job-seeking signals).</span>
                    </label>
                  </div>
                )}
              </motion.div>

              <div className="flex items-center justify-between mt-8 pt-5 border-t border-border gap-3 flex-wrap">
                <Button variant="ghost" onClick={back} disabled={step === 0} data-testid="manual-wizard-back-button">
                  <ArrowLeft size={16} className="mr-2" /> Back
                </Button>
                <div className="flex items-center gap-3 ml-auto">
                  {missingForStep() && (
                    <p
                      className="text-[12px] text-amber-600 dark:text-amber-400 max-w-[300px] text-right"
                      data-testid="manual-wizard-missing-hint"
                    >
                      {missingForStep()}
                    </p>
                  )}
                  {step < STEPS.length - 1 ? (
                    <Button onClick={next} disabled={!canNext()} className="rounded-xl" data-testid="manual-wizard-next-button">
                      Next <ArrowRight size={16} className="ml-2" />
                    </Button>
                ) : (
                  <Button
                    onClick={onSubmit}
                    disabled={!canNext() || submitting}
                    className="rounded-xl"
                    data-testid="wizard-submit-button"
                  >
                    {submitting ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        <Sparkles size={16} className="mr-2" />
                        {'Audit my profile'}
                      </>
                    )}
                  </Button>
                )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
